import { equals, StateListenerRegistry } from '../base/redux';

import { debounce } from 'lodash';
import { getCurrentConference } from '../base/conference';
import { isCheckPermission } from '../base/localuser';
import { getLayoutParticipant, getLocalParticipant, getPinnedParticipantsByStatus, getRemoteParticipantsByStatus, PERMISSION } from '../base/participants';
import { setVideoLayoutProperty, setVisibleRemoteCounts } from './actions';
import { shareLayoutList, videoLayoutMode } from './constants';
import { getCurrentMode, getGridVisibleCount, getLayoutWidthAndHeight, getVisibleParticipantCount, updateRemoteParticipants } from './functions';

function changeVisibleCount(store) {
    const { dispatch, getState } = store;
    const state = getState();

    const conferenceJoined = getCurrentConference(getState());
    if (!conferenceJoined) return;

    let { gridCount, visibleParticipantsCount } = state['features/video-layout-mode'];
    let { layoutWidth, layoutHeight } = getLayoutWidthAndHeight(state);

    const pinnedParticipantCount = getPinnedParticipantsByStatus(state, true).length;
    const mode = getCurrentMode(state);

    let count = gridCount;
    if (APP.mateManagement.getScreenSharing()) {
        const { screenModal } = state['features/video-layout-mode'];
        if (screenModal) count = 4;
        else count = Math.floor(layoutHeight / 98);
    } else {
        if (layoutWidth <= 450) {
            // if (mode === videoLayoutMode.seminar) count = 1;
            // else count = 10;
            count = 10;
        } else {
            switch (mode) {
                case videoLayoutMode.pin:
                case videoLayoutMode.voice:
                    count = Math.floor(layoutWidth / 172);
                    break;
        
                case videoLayoutMode.document:
                case videoLayoutMode.white:
                case videoLayoutMode.note:
                    count = Math.floor(layoutWidth / 172);
                    break;
        
                case videoLayoutMode.screen:
                    count = Math.floor(layoutHeight / 98) + 1;
                    break; 
            }
        }
    }
    
    const isPinMode = (mode === videoLayoutMode.pin || mode === videoLayoutMode.voice || mode === videoLayoutMode.seminar );
    if ((count - pinnedParticipantCount > 0 || isPinMode) && mode !== videoLayoutMode.grid) {        
        count = isPinMode ? count : count - pinnedParticipantCount;
        dispatch(setVideoLayoutProperty({ includePin: false }));
    } else {
        dispatch(setVideoLayoutProperty({ includePin: true }));
    }
    
    const maxCount = Math.max(count, 1);
    if (maxCount !== visibleParticipantsCount) dispatch(setVisibleRemoteCounts(maxCount));
    
    
    updateRemoteParticipants(store);
    updateLayoutPaging(store);
}

function updateLayoutPaging({ dispatch, getState }) {    
    const state = getState();
    const { isShowArrow, page, remoteParticipants, visibleParticipantsCount } = state['features/video-layout-mode'];
    const count = visibleParticipantsCount < 1 ? 1 :  visibleParticipantsCount;
    
    const participantCount = remoteParticipants.length;    
    const minusPage = getVisibleParticipantCount(state) < 1 ? 1 : 0;
    let maxPage = Math.ceil(participantCount / count) - minusPage;
    maxPage = maxPage > 1 ? maxPage : 1;

    let currentPage = maxPage < page ? page - 1 : page;
    currentPage = currentPage <= 0 ? 1 : currentPage;
    
    dispatch(setVideoLayoutProperty({
        isShowArrow: {
            ...isShowArrow,
            next: currentPage < maxPage ? true : false,
            prev: currentPage > 1 ? true : false,
            maxPage
        }, page: currentPage 
    }));
}

StateListenerRegistry.register(state => state['features/group'].lastUpdateTimestamp,
    (lastUpdateTimestamp, store) => {
        updateRemoteParticipants(store);
    });

StateListenerRegistry.register(state => state['features/base/participants'].remote.length,
    (remoteParticipants, store) => {
        changeVisibleCount(store);
    }, {
        deepEquals: true
    });

StateListenerRegistry.register(state => state['features/video-layout-mode'].page,
    (page, store) => {
        updateRemoteParticipants(store);
    }
);

StateListenerRegistry.register(state => getGridVisibleCount(state),
    (gridCount, store) => {
        const { getState } = store;
        const conferenceJoined = getCurrentConference(getState());
        if (!conferenceJoined) return;

        changeVisibleCount(store);
    }
);
 
StateListenerRegistry.register(state => state['features/base/participants'].dominantSpeaker,
    (dominantSpeaker, store) => {
        const { getState } = store;
        const conferenceJoined = getCurrentConference(getState());
        if (!conferenceJoined) return;

        changeVisibleCount(store);
    }
);

StateListenerRegistry.register(state => getLocalParticipant(state),
    (conferenceJoined, store) => {
        if (!conferenceJoined) return;
        
        changeVisibleCount(store);
    }
);

StateListenerRegistry.register(state => getCurrentConference(state),
    (conferenceJoined, store) => {
        if (!conferenceJoined) return;
        
        changeVisibleCount(store);
    }
);

StateListenerRegistry.register(state => getCurrentMode(state),
    (mode, store, prevMode) => {
        const state = store.getState();
        const conferenceJoined = getCurrentConference(state);
        if (!conferenceJoined) return;

        changeVisibleCount(store);
        APP.UI.showLoading(false);
        if (mode !== prevMode) {
            if (shareLayoutList.indexOf(mode) !== -1 && shareLayoutList.indexOf(prevMode) !== -1) {
                if (mode !== videoLayoutMode.screen) return;
            }
            
            store.dispatch(setVideoLayoutProperty({ page: 1 }));
        }
    }
);

StateListenerRegistry.register(state => [...state["features/base/participants"].pinnedParticipant],
    (pinnedParticipant, store) => {
        changeVisibleCount(store);
    }, {
        deepEquals: true
    }
);

StateListenerRegistry.register(state => getRemoteParticipantsByStatus(state),
    (participant, store, prevParticipant) => {
        changeVisibleCount(store);
    }, {
        deepEquals: true
    }
);

StateListenerRegistry.register(state => state['features/screen-share'].sharingVideo,
    debounce((sharingVideo, store, prevSharingVideo) => {
        const { isDualMonitor } = store.getState()["features/video-layout-mode"];
        if (sharingVideo && isDualMonitor) {
            store.dispatch(setVideoLayoutProperty({ isDualMonitor: false }));
        }

        if (equals(sharingVideo, prevSharingVideo)) return;

        changeVisibleCount(store); 
    }, 500)
);

StateListenerRegistry.register(state => getLayoutWidthAndHeight(state), 
    ({ layoutWidth, layoutHeight }, store, preStyle) => {
        if (!(layoutWidth || layoutHeight)) return;
        const state = store.getState();
        const conferenceJoined = getCurrentConference(state);
        if (!conferenceJoined) return;
        
        if (equals(preStyle.layoutWidth, layoutWidth) && equals(preStyle.layoutHeight, layoutHeight)) return;
        
        if (layoutWidth !== 0) changeVisibleCount(store); 
        if (layoutHeight !== 0) changeVisibleCount(store); 
    }, {
        deepEquals: true
    }
)